<script setup lang="ts">
/**
 * One proposed value (plan §6 field provenance): label, five-status chip, value with unit,
 * evidence, history, Confirm / Change / I'm not sure. Criticals only by an explicit act.
 */
import { computed, ref } from 'vue';
import { displayFor, fmtMm, parseMm, unresolveField } from '@/domain';
import Icon from './Icon.vue';
import ProvChip from './ProvChip.vue';
import { app, snap, ui } from '@/app/state';
import { announce, toast } from '@/app/announce';
import { envelopeIssue, useNearest } from '@/app/fixes';
import { commitDim, commitShelves, confirmKey, limits, limitsFor, setEvenTo } from '@/app/edits';
import { focusSoon, go, labelOf, lockReason, pendingCritical, visibleFieldKeys } from '@/app/navigation';
import { fmtDate, PROV } from '@/app/format';

const props = defineProps<{ field: string }>();
const k = computed(() => props.field);
const f = computed(() => snap.value.fields[k.value]!);
const d = computed(() => snap.value.design);
const b = computed(() => snap.value.build);
const NEEDS = ['proposed', 'published', 'unresolved'];
const needs = computed(() => NEEDS.includes(f.value.status));
const crit = computed(() => f.value.critical);
const editing = computed(() => ui.checkEditing === k.value);
const isDim = computed(() => k.value === 'W' || k.value === 'H' || k.value === 'D');
const lim = computed(() => { void snap.value; return isDim.value ? limitsFor(k.value as 'W' | 'H' | 'D') : null; });
const stockMeasured = computed(() => snap.value.stockInfo.thicknessTicks);
const stockNominal = computed(() => snap.value.stockInfo.nominalThicknessTicks);
const backMm = computed(() => fmtMm(snap.value.build.thickness.back));
/** FND-02: a proposed or published size outside the construction's limits, flagged before it is confirmed. */
const env = computed(() => { void snap.value; return isDim.value ? envelopeIssue(k.value as 'W' | 'H' | 'D') : null; });
/** Feedback after Save (FND-12): "Confirmed 901.7 mm" when the value was already that. */
const saved = ref<string | null>(null);

const confirmLabel = computed(() => {
  if (!crit.value) return 'Looks right';
  if (k.value === 'stockThickness') return `Confirm ${fmtMm(stockNominal.value)} mm`;
  return `Confirm ${fmtMm(d.value[k.value as 'W' | 'H' | 'D'], { unit: true })}`;
});

const ev = computed(() => {
  const e = (f.value.evidence ?? {}) as { quote?: string; note?: string; conversion?: string };
  const conversion = e.conversion ?? (e.quote && e.note && /=/.test(e.note) ? e.note : null);
  const note = e.note && e.note !== conversion ? e.note : null;
  return { quote: e.quote ?? null, conversion, note };
});
const history = computed(() => (f.value.history || []).slice(-3));

// Opening strips: photo (proposal ratios, bay 1) versus design (bay 1)
const ratios = computed(() => (ui.analysis.status !== 'manual' && Array.isArray(snap.value.fields.openingRatios?.value) ? (snap.value.fields.openingRatios!.value as number[]) : []));
const bay1 = computed(() => b.value.openings.filter((o) => (o.bay ?? 0) === 0));
const total = computed(() => bay1.value.reduce((s, o) => s + o.clearMm, 0) || 1);
const isParam = computed(() => ['bays', 'back', 'plinth'].includes(k.value));

const draft = (id: string, fallback: string) => (id in ui.drafts ? ui.drafts[id]! : fallback);
const onInput = (e: Event) => { const t = e.target as HTMLInputElement; ui.drafts[t.id] = t.value; };
const errKey = computed(() => (k.value === 'shelfCount' ? 'n' : k.value));
const err = computed(() => ui.fieldErrors[errKey.value] ?? null);

function nextFocusAfter(key: string): string {
  const keys = visibleFieldKeys();
  const i = keys.indexOf(key);
  const order = keys.slice(i + 1).concat(keys.slice(0, i));
  const next = order.find((x) => NEEDS.includes(app.store.fields[x]?.status ?? ''));
  if (next) return `[data-confirm="${next}"], [data-use-limit="${next}"]`;
  return pendingCritical().length ? `#change-${key}` : '#dock [data-act="continue"]';
}
function confirm() {
  confirmKey(k.value, labelOf(k.value));
  announce(`${labelOf(k.value)} confirmed.${pendingCritical().length ? '' : ' All critical values confirmed.'}`);
  focusSoon(nextFocusAfter(k.value));
}
/** Out of range: bring it inside the construction's limits (an edit, so it counts as confirmed by you). */
function useLimit() {
  const e = env.value;
  if (!e) return;
  useNearest(e.key);
  saved.value = null;
  announce(`${labelOf(k.value)} set to ${fmtMm(e.nearest)} mm, inside this construction's limits.`);
  focusSoon(nextFocusAfter(k.value));
}
/** Out of range, kept: confirmed as the user's number; Shape then offers the fix and Pack stays off. */
function keepIt() {
  const e = env.value;
  if (!e) return;
  confirmKey(k.value, labelOf(k.value));
  toast(`Kept ${fmtMm(e.value)} mm. Check it with your maker; Shape will offer ${fmtMm(e.nearest)} mm until it fits this construction.`);
  announce(`${labelOf(k.value)} kept at ${fmtMm(e.value)} mm. The design stays invalid until it is inside ${fmtMm(e.min)} to ${fmtMm(e.max)} mm.`);
  focusSoon(nextFocusAfter(k.value));
}
function change() {
  saved.value = null;
  ui.checkEditing = k.value;
  focusSoon(`.vcard[data-field="${k.value}"] .vc-edit input, .vcard[data-field="${k.value}"] .vc-edit button`);
  setTimeout(() => document.querySelector(`.vcard[data-field="${k.value}"]`)?.scrollIntoView({ block: 'center', behavior: 'smooth' }), 60);
}
function cancel() {
  const key = ui.checkEditing ?? k.value;
  ui.checkEditing = null;
  delete ui.fieldErrors[key === 'shelfCount' ? 'n' : key];
  delete ui.drafts[`chk-${key}`];
  delete ui.drafts['chk-n'];
  focusSoon(`#change-${key}`);
}
function unsure() {
  const key = k.value;
  ui.checkEditing = null;
  delete ui.fieldErrors[key === 'shelfCount' ? 'n' : key];
  app.store.apply(() => ({ fields: unresolveField(app.store.fields, key, { note: 'needs a value' }) }), { label: `${labelOf(key)} unsure` });
  announce(`${labelOf(key)} marked as unsure.`);
  focusSoon(`#change-${key}`);
}
const evenChoice = ref<boolean | null>(null);
function save() {
  const key = k.value;
  let ok = false;
  saved.value = null;
  if (isDim.value) {
    const raw = (document.getElementById(`chk-${key}`) as HTMLInputElement).value;
    const r = parseMm(raw);
    const cur = d.value[key as 'W' | 'H' | 'D'];
    if (r.ok && r.ticks === cur) {
      // FND-12: the same value after unit normalisation is a confirmation, said so
      delete ui.fieldErrors[key];
      delete ui.drafts[`chk-${key}`];
      if (NEEDS.includes(f.value.status)) confirmKey(key, labelOf(key));
      saved.value = `Confirmed ${fmtMm(cur)} mm`;
      announce(`${labelOf(key)}: confirmed ${fmtMm(cur)} mm.`);
      ui.checkEditing = null;
      focusSoon(nextFocusAfter(key));
      return;
    }
    ok = commitDim(key as 'W' | 'H' | 'D', raw, `chk-${key}`);
    if (ok) saved.value = `Saved ${fmtMm(d.value[key as 'W' | 'H' | 'D'])} mm`;
    else if (!ui.fieldErrors[key]) ui.fieldErrors[key] = `${labelOf(key)} was not changed. Enter a size in millimetres, or Cancel.`;
  }
  else if (key === 'shelfCount') ok = commitShelves((document.getElementById('chk-n') as HTMLInputElement).value);
  else if (key === 'even') {
    const even = evenChoice.value ?? !!d.value.even;
    if (even !== !!d.value.even) setEvenTo(even);
    else confirmKey('even', 'even');
    ok = true;
  }
  if (ok) { ui.checkEditing = null; focusSoon(`#change-${key}`); }
  else focusSoon(`#chk-${key === 'shelfCount' ? 'n' : key}`);
}
function onKey(e: KeyboardEvent) {
  if (e.key === 'Enter' && (e.target as HTMLElement).matches('.vc-edit input.input')) { e.preventDefault(); save(); }
  if (e.key === 'Escape' && editing.value) { e.preventDefault(); cancel(); }
}
const NOTES: Record<string, string> = {
  family: 'If this is another style, mark it unsure and set it up by hand from the closest style.',
  material: 'Choose birch plywood, sheesham or oak in Look; a different material changes the stock and the cut list.',
  finish: 'Choose oiled, satin or matt in Look; the finish never changes what gets cut.',
  openings: 'Opening heights are set in Shape, on the model or by typing them.',
  openingRatios: 'Opening heights are set in Shape, on the model or by typing them.',
  bays: 'Bays and their widths are set in Shape.',
  back: 'The back is set in Shape: overlay, rebated or none.',
  plinth: 'The plinth height is set in Shape (0 for none).',
};
const note = computed(() => (k.value === 'stockThickness'
  ? `${snap.value.stockInfo.material} ${fmtMm(stockNominal.value)} mm (measured ${(stockMeasured.value / 10).toFixed(1)} mm). Change the material, and with it the thickness, in Look.`
  : NOTES[k.value] ?? ''));
</script>

<template>
  <article
    class="vcard"
    :class="{ 'is-critical': crit, needs, 'is-editing': editing }"
    :data-field="field"
    :aria-labelledby="`vc-${field}`"
    @keydown="onKey"
  >
    <div class="vc-top"><h3 :id="`vc-${field}`" class="vc-label">{{ labelOf(field) }}</h3><ProvChip :status="f.status" /></div>
    <p class="vc-value">
      <template v-if="isDim">{{ fmtMm(d[field as 'W' | 'H' | 'D']) }}<span class="unit">mm{{ field === 'D' ? ` · includes the ${backMm} mm back` : '' }}</span></template>
      <template v-else-if="field === 'shelfCount' && b.bays.length > 1">{{ b.bays.every((x) => x.n === b.bays[0]!.n) ? d.n : b.bays.map((x) => x.n).join(' / ') }}<span class="unit">{{ b.bays.every((x) => x.n === b.bays[0]!.n) ? 'shelves per bay' : 'shelves, bay by bay' }}</span></template>
      <template v-else-if="field === 'shelfCount'">{{ d.n }}<span class="unit">{{ d.n === 1 ? 'shelf' : 'shelves' }}</span></template>
      <template v-else-if="field === 'even'">{{ d.even ? 'Even' : 'Uneven' }}</template>
      <span v-else-if="field === 'openings' || field === 'openingRatios'" class="vc-small">{{ bay1.map((o) => fmtMm(o.clear)).join(' / ') }}<span class="unit">mm, bottom up{{ b.bays.length > 1 ? ', bay 1' : '' }}</span></span>
      <span v-else-if="isParam" class="vc-small">{{ displayFor(d, field)[1] }}</span>
      <template v-else-if="field === 'stockThickness'">{{ fmtMm(stockNominal) }}<span class="unit">mm · measured {{ (stockMeasured / 10).toFixed(1) }}</span></template>
      <span v-else class="vc-small">{{ f.display }}</span>
    </p>

    <div v-if="field === 'openings' || field === 'openingRatios'" class="strips" aria-hidden="true">
      <div v-if="ratios.length" class="strip">
        <span class="strip-l">Photo</span>
        <div class="strip-bars"><span v-for="(r, i) in ratios" :key="i" :style="{ flex: r }" :title="`Opening ${i + 1}: ${(r * 100).toFixed(1)}%`">{{ (r * 100).toFixed(0) }}%</span></div>
      </div>
      <div class="strip strip--you">
        <span class="strip-l">Design</span>
        <div class="strip-bars"><span v-for="o in bay1" :key="o.index" :style="{ flex: o.clearMm / total }" :title="`Opening ${o.number}: ${fmtMm(o.clear)} mm`">{{ fmtMm(o.clear) }}</span></div>
      </div>
      <p class="strip-axis"><span>Bottom</span><span>Top</span></p>
    </div>

    <p class="vc-ev">
      <template v-if="f.source === 'manual'"><Icon name="pencil" /><span>Starting value from the style’s plainest version, not from your reference.</span></template>
      <template v-else>
        <template v-if="ev.quote"><Icon name="quote" /><span>“<mark :data-fact="field" :class="{ 'is-lit': ui.litFact === field }">{{ ev.quote }}</mark>” in the product text{{ ev.conversion ? ` · ${ev.conversion}` : '' }}</span></template>
        <template v-if="ev.note"><Icon name="image" /><span>{{ ev.note }}</span></template>
      </template>
    </p>
    <p v-if="f.status === 'unresolved' && f.source !== 'manual'" class="vc-note"><Icon name="question" /> Marked unsure{{ f.note ? `: ${f.note}` : '' }}. Check it with your maker.</p>
    <div v-if="env" class="vc-env" role="note" :data-envelope="field">
      <p><Icon name="alert" /><span><strong>{{ env.message }}.</strong> {{ needs ? 'Confirming it as it is leads to an invalid design.' : 'The design stays invalid until it fits; Shape offers the fix.' }}</span></p>
      <div class="row-acts">
        <button type="button" class="btn btn--sm btn--ink" :data-use-limit="field" @click="useLimit">Use {{ fmtMm(env.nearest) }} mm</button>
        <button v-if="needs" type="button" class="btn btn--sm btn--quiet" :data-keep="field" @click="keepIt">Keep {{ fmtMm(env.value) }} and check with your maker</button>
      </div>
    </div>
    <p v-if="saved && !editing" class="vc-saved" role="status" :data-saved="field"><Icon name="check" /> {{ saved }}</p>
    <p v-if="crit && needs" class="vc-crit"><Icon name="lock" /><span>Fabrication-critical: the maker cuts to this number, so only you can confirm it.</span></p>
    <ol v-if="!needs && (f.history || []).length >= 2" class="vc-hist" aria-label="History">
      <li v-for="(x, i) in history" :key="i"><span :class="['prov-word', `prov-word--${x.status}`]">{{ PROV[x.status]?.label ?? x.status }}</span> {{ x.display }}{{ x.at ? ' · ' : '' }}<time v-if="x.at">{{ fmtDate(x.at) }}</time></li>
    </ol>

    <div v-if="editing" class="vc-edit">
      <template v-if="isDim && lim">
        <label class="lbl" :for="`chk-${field}`">New {{ labelOf(field).toLowerCase() }} <span class="muted">{{ fmtMm(lim.min) }}–{{ fmtMm(lim.max) }} mm{{ field === 'D' ? `, includes the ${backMm} mm back` : '' }}</span></label>
        <div class="input-unit">
          <input
            :id="`chk-${field}`"
            class="input mono"
            inputmode="decimal"
            autocomplete="off"
            :value="draft(`chk-${field}`, fmtMm(d[field as 'W' | 'H' | 'D']))"
            :aria-invalid="err ? 'true' : undefined"
            :aria-describedby="err ? `err-${field}` : undefined"
            @input="onInput"
          ><span>mm</span>
        </div>
        <p v-if="err" :id="`err-${field}`" class="field-err" role="alert"><Icon name="alert" /><span>{{ err }}</span></p>
        <div class="row-acts">
          <button type="button" class="btn btn--sm btn--ink" :data-save="field" @click="save">Save</button>
          <button type="button" class="btn btn--sm btn--quiet" :data-cancel="field" @click="cancel">Cancel</button>
          <button type="button" class="btn btn--sm btn--text" :data-unsure="field" @click="unsure">I’m not sure</button>
        </div>
      </template>
      <template v-else-if="field === 'shelfCount'">
        <label class="lbl" for="chk-n">Number of shelves{{ b.bays.length > 1 ? ' in bay 1' : '' }} <span class="muted">{{ limits().n.min }}–{{ limits().n.max }}, spaced evenly</span></label>
        <div class="input-unit">
          <input id="chk-n" class="input mono" inputmode="numeric" autocomplete="off" :value="draft('chk-n', String(d.n))" :aria-invalid="ui.fieldErrors.n ? 'true' : undefined" :aria-describedby="ui.fieldErrors.n ? 'err-n' : undefined" @input="onInput"><span>shelves</span>
        </div>
        <p v-if="ui.fieldErrors.n" id="err-n" class="field-err" role="alert"><Icon name="alert" /><span>{{ ui.fieldErrors.n }}</span></p>
        <div class="row-acts">
          <button type="button" class="btn btn--sm btn--ink" data-save="shelfCount" @click="save">Save</button>
          <button type="button" class="btn btn--sm btn--quiet" data-cancel="shelfCount" @click="cancel">Cancel</button>
          <button type="button" class="btn btn--sm btn--text" data-unsure="shelfCount" @click="unsure">I’m not sure</button>
        </div>
      </template>
      <template v-else-if="field === 'even'">
        <fieldset class="radio-row">
          <legend class="lbl">Openings</legend>
          <label><input type="radio" name="chk-even" value="even" :checked="d.even" @change="evenChoice = true"> Even</label>
          <label><input type="radio" name="chk-even" value="uneven" :checked="!d.even" @change="evenChoice = false"> Uneven, like the photo</label>
        </fieldset>
        <div class="row-acts">
          <button type="button" class="btn btn--sm btn--ink" data-save="even" @click="save">Save</button>
          <button type="button" class="btn btn--sm btn--quiet" data-cancel="even" @click="cancel">Cancel</button>
        </div>
      </template>
      <template v-else>
        <p class="muted small">{{ note }}</p>
        <div class="row-acts">
          <button v-if="(field.startsWith('opening') || isParam) && !lockReason('shape')" type="button" class="btn btn--sm btn--ink" data-go="shape" @click="go('shape')">Adjust in Shape</button>
          <button type="button" class="btn btn--sm btn--quiet" :data-unsure="field" @click="unsure">Mark as unsure</button>
          <button type="button" class="btn btn--sm btn--text" :data-cancel="field" @click="cancel">Cancel</button>
        </div>
      </template>
    </div>
    <div v-else class="vc-acts">
      <button v-if="needs && !env" :id="`confirm-${field}`" type="button" class="btn btn--sm btn--soft" :data-confirm="field" @click="confirm"><Icon name="check" /> {{ confirmLabel }}</button>
      <button :id="`change-${field}`" type="button" class="btn btn--sm btn--quiet" :data-change="field" @click="change">Change</button>
    </div>
  </article>
</template>
